import { Alert, Box, Button, Card, Group, Tabs, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import getErrorMessage from '../../../helpers/getErrorMessage';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import {
  emptyForm,
  formErrors,
  placeholdersIn,
  RESOLVED_IDENTITY_PLACEHOLDERS,
  toBody,
  toForm,
} from '../connectorConfig';
import { emptyArg, MAX_ARGS } from '../connectorToolConfig';
import { ConnectorStatusBadge } from '../shared';
import TransitionConfirm from '../TransitionConfirm';
import { confirmFor, transitionMessage } from '../transitions';
import { capabilityLabel, nextSlot, setSlot, slotsOf } from './capabilities';
import { CapabilityStep } from './CapabilityPanels';
import {
  platformArgs,
  probeInputsFor,
  readTestValues,
  requestPlaceholders,
  shopperInputs,
  writeTestValues,
} from './placeholders';
import { probeScenarios } from './probeSuggestions';
import RequestBar from './RequestBar';
import RequestStep from './RequestStep';
import ResponsePane from './ResponsePane';
import SetLiveDialog from './SetLiveDialog';
import StartMenu from './StartMenu';
import { templateForm } from './templates';
import { runSetLive } from './setLive';
import { AgentStep, RowsStep, SafetyStep } from './StepPanels';
import { errorList, snapshotOf, stepsDone, toolOf } from './workspaceState';
import classes from './workspace.module.css';

const STEPS = {
  tool: [
    { value: 'request', label: 'Request', Panel: RequestStep },
    { value: 'rows', label: 'Rows', Panel: RowsStep },
    { value: 'agent', label: 'Agent', Panel: AgentStep },
    { value: 'safety', label: 'Safety', Panel: SafetyStep },
  ],
  // A capability's agent tool is the platform's own: its third step says what it answers instead.
  capability: [
    { value: 'capability', label: 'Capability', Panel: CapabilityStep },
    { value: 'request', label: 'Request', Panel: RequestStep },
    { value: 'rows', label: 'Rows', Panel: RowsStep },
    { value: 'safety', label: 'Safety', Panel: SafetyStep },
  ],
};

const notifyUndo = (message, undo) => {
  const id = `workspace-start-${Date.now()}`;
  notifications.show({
    id,
    message: (
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm">{message}</Text>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() => {
            undo();
            notifications.hide(id);
          }}
        >
          Undo
        </Button>
      </Group>
    ),
  });
};

// The connector workspace: steps on the left, the last Send on the right. Send runs the unsaved
// draft (C7); Set live saves, probes the saved row with the same inputs, then moves it to live.
// A store tool (kind 'tool') declares its own agent tool; a platform capability fills its
// contract's result fields as slots.
const ConnectorWorkspace = ({ apiBase, accountId, domainId, instance, kind = 'tool', onBack, onSaved }) => {
  const fetch = useFetch();
  const isTool = kind === 'tool';
  const steps = STEPS[kind];

  const [current, setCurrent] = useState(instance);
  const [form, setForm] = useState(() => (instance ? toForm(instance) : { ...emptyForm(), mode: kind }));
  const [testValues, setTestValues] = useState(() => readTestValues(instance?.id));
  const [step, setStep] = useState(steps[0].value);
  const [contracts, setContracts] = useState([]);
  const [slot, setSlotName] = useState(null);
  const [sub, setSub] = useState('args');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [lastSend, setLastSend] = useState(null);
  const [dialog, setDialog] = useState(null);

  // Kept per saved id only, so one new tool's test values never seed the next.
  useEffect(() => {
    if (current?.id) writeTestValues(current.id, testValues);
  }, [current?.id, testValues]);

  // The catalogue is the same for every store; only the contracts the http type serves are offered.
  useEffect(() => {
    if (isTool) return;
    fetch('/api/admin/connectors/contracts')
      .then((res) => {
        const served = (res.contracts || []).filter((contract) => contract.httpSupported);
        setContracts(served);
        if (served.length === 1)
          setForm((previous) => (previous.capability ? previous : { ...previous, capability: served[0].capability }));
      })
      .catch((err) => setError(getErrorMessage(err, 'Could not load the contract catalogue.')));
  }, [fetch, isTool]);

  const readOnly = current?.status === 'live';
  // A capability's {{args.*}} are filled by its handler, so whatever the request reads is known.
  const argNames = isTool ? form.tool.argRows.map((row) => row.name.trim()).filter(Boolean) : platformArgs(form);
  const contract = contracts.find((candidate) => candidate.capability === form.capability) || null;
  const inputs = probeInputsFor(form, toolOf(form), testValues);
  const snapshot = snapshotOf(form, testValues);
  const dirty = !current || JSON.stringify(toBody(toForm(current))) !== JSON.stringify(toBody(form));
  const canSetLive = !readOnly && !!lastSend?.ok && lastSend.snapshot === snapshot;
  const done = stepsDone(form, argNames, contract);

  const setField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));
  const setToolField = (field, value) =>
    setForm((previous) => ({ ...previous, tool: { ...previous.tool, [field]: value } }));
  const setTestValue = (name, value) => setTestValues((previous) => ({ ...previous, [name]: value }));
  // A suggested test fills the arguments and shows them; Send stays the reader's.
  const tryScenario = (scenario) => {
    setTestValues((previous) => ({ ...previous, ...scenario.values }));
    setStep('request');
    setSub('args');
  };

  const keep = (saved) => {
    const next = toForm(saved);
    // The server fills defaults in, which is not an edit: a fresh passing Send stays fresh.
    if (lastSend?.snapshot === snapshot) setLastSend({ ...lastSend, snapshot: snapshotOf(next, testValues) });
    setForm(next);
    setCurrent(saved);
    onSaved(saved);
  };

  const check = () => {
    const found = formErrors(form, contract);
    setErrors(found);
    const list = errorList(found);
    if (!list.length) return true;
    setError('Some fields need fixing first.');
    setStep(steps.some((candidate) => candidate.value === list[0].step) ? list[0].step : 'request');
    return false;
  };

  const replaceForm = (next, values = {}) => {
    const before = { form, testValues };
    setForm(next);
    setTestValues((previous) => ({ ...previous, ...values }));
    return () => {
      setForm(before.form);
      setTestValues(before.testValues);
    };
  };

  const applyTemplate = (template) => {
    notifyUndo(`Template: ${template.label}. Add the API host after https://`, replaceForm(templateForm(template)));
    setStep('request');
  };

  const showExample = (body) => {
    setResult({ example: true, body });
    setSendError(null);
    setLastSend(null);
    if (!isTool) setStep('rows');
  };

  const slots = isTool
    ? null
    : {
        slot,
        fill: (path) => {
          const mapRows = setSlot(form.mapRows, slot, path);
          setField('mapRows', mapRows);
          setSlotName(nextSlot(slotsOf(contract, mapRows), slot));
        },
      };

  const applyUrlExample = (example) => {
    const before = form;
    const declared = new Set(argNames);
    const text = `${example.url} ${example.body || ''}`;
    // A capability declares no arguments; its handler fills whatever {{args.*}} the URL reads.
    const added = (isTool ? placeholdersIn(text) : [])
      .map((name) => /^args\.([^.]+)$/.exec(name)?.[1])
      .filter((name) => name && !declared.has(name) && declared.add(name));
    setForm((previous) => ({
      ...previous,
      method: example.method,
      url: example.url,
      body: example.body || '',
      tool: {
        ...previous.tool,
        argRows: [...previous.tool.argRows, ...added.map((name) => ({ ...emptyArg(), name, required: true }))].slice(
          0,
          MAX_ARGS
        ),
      },
    }));
    if (added.length) {
      setStep('request');
      setSub('args');
    }
    return () => setForm(before);
  };

  const send = () => {
    const missing = form.tool.argRows.filter(
      (row) => row.required && row.type !== 'boolean' && !String(testValues[`args.${row.name.trim()}`] ?? '').trim()
    );
    if (missing.length) {
      setSendError(
        `${missing.map((row) => row.name.trim() || 'An unnamed argument').join(', ')} needs a test value before Send.`
      );
      setResult(null);
      setStep('request');
      setSub('args');
      return;
    }

    const { contracts: sentContracts, config, tool, domainSecretId } = toBody(form);
    const sent = snapshot;
    setSending(true);
    setSendError(null);
    fetch(`${apiBase}/probe-draft`, {
      method: 'POST',
      body: JSON.stringify({
        instanceId: current?.id,
        contracts: sentContracts,
        config,
        tool,
        domainSecretId,
        ...inputs,
      }),
    })
      .then((res) => {
        setResult(res);
        setLastSend({ ok: res.ok === true, snapshot: sent });
      })
      .catch((err) => {
        setResult(null);
        setLastSend(null);
        setSendError(getErrorMessage(err, 'The draft could not be sent.'));
      })
      .finally(() => setSending(false));
  };

  const save = () => {
    if (!check()) return;
    setSaving(true);
    setError(null);
    fetch(current ? `${apiBase}/${current.id}` : apiBase, {
      method: current ? 'PUT' : 'POST',
      body: JSON.stringify(toBody(form)),
    })
      .then((res) => keep(res.instance))
      .catch((err) => setError(getErrorMessage(err, 'The connector could not be saved.')))
      .finally(() => setSaving(false));
  };

  const setLive = (onStep) =>
    runSetLive({ fetch, apiBase, instanceId: current?.id, body: toBody(form), inputs, onStep, onSaved: keep }).then(
      (outcome) => {
        if (outcome.ok)
          notifications.show({
            title: 'Connector live',
            message: transitionMessage('live', outcome.instance),
            color: 'success',
          });
        return outcome;
      }
    );

  const disable = () =>
    fetch(`${apiBase}/${current.id}/transition`, { method: 'POST', body: JSON.stringify({ to: 'disabled' }) }).then(
      (res) => keep(res.instance)
    );

  const ws = {
    isTool,
    contracts,
    contract,
    slot,
    setSlot: setSlotName,
    platformNames: isTool ? [] : platformArgs(form),
    // A capability that keys on the shopper but reads no identity the server can fill.
    noIdentity:
      !isTool &&
      !!contract?.identityFields?.length &&
      !requestPlaceholders(form).some((name) => RESOLVED_IDENTITY_PLACEHOLDERS.includes(name)),
    form,
    setField,
    setToolField,
    errors,
    readOnly,
    argNames,
    shopperNames: shopperInputs(form),
    testValues,
    setTestValue,
    sub,
    setSub,
    accountId,
    domainId,
    fired: result?.rule,
    onSecretsError: (err) => setError(getErrorMessage(err, 'Could not load the secrets of this store.')),
  };
  const { Panel } = steps.find((candidate) => candidate.value === step) || steps[0];
  const name = form.tool.name.trim();
  const title = isTool
    ? name
      ? `store_${name}`
      : 'New store tool'
    : form.capability
      ? capabilityLabel(form.capability)
      : 'New platform capability';

  const setLiveButton = (
    <Button size="xs" disabled={!canSetLive} onClick={() => check() && setDialog('live')}>
      Set live
    </Button>
  );

  return (
    <Card withBorder mt="sm" padding={0}>
      <Group gap="xs" p="sm" wrap="wrap">
        <Text size="xs" c="dimmed">
          Connectors · {isTool ? 'Store tools' : 'Platform capabilities'} ·{' '}
          <Text span ff="monospace" fw={600} c="var(--mantine-color-text)">
            {title}
          </Text>
        </Text>
        {current && <ConnectorStatusBadge status={current.status} />}
        <Text size="xs" c="dimmed">
          {dirty ? 'unsaved changes' : 'saved'}
        </Text>
        <Box style={{ flex: 1 }} />
        <Button size="xs" variant="default" onClick={onBack}>
          Back
        </Button>
        {readOnly ? (
          <Button size="xs" variant="light" color="danger" onClick={() => setDialog('disable')}>
            Disable to edit
          </Button>
        ) : (
          <>
            <StartMenu
              kind={kind}
              form={form}
              secretsBase={`/api/admin/accounts/${accountId}/domains/${domainId}/secrets`}
              onImport={({ form: next, testValues: values }) => {
                replaceForm(next, values);
                setStep('request');
                // Remounts the Auth tab so its secret picker re-reads the list with the new secret.
                setSub('args');
              }}
              onTemplate={applyTemplate}
              onExample={showExample}
            />
            <Button size="xs" variant="default" loading={saving} onClick={save}>
              Save draft
            </Button>
            {canSetLive ? (
              setLiveButton
            ) : (
              <Tooltip label="Needs a passing Send with no edits since" withArrow>
                <Box>{setLiveButton}</Box>
              </Tooltip>
            )}
          </>
        )}
      </Group>

      {readOnly && (
        <Alert color="info" variant="light" radius={0} py={6}>
          Live: read-only. Send still runs the config with your test values.
        </Alert>
      )}
      {current && !readOnly && current.status !== 'draft' && (
        <Alert color="info" variant="light" radius={0} py={6}>
          Saving returns this connector to draft; Set live probes it again.
        </Alert>
      )}

      <Tabs value={step} onChange={(value) => setStep(value || steps[0].value)}>
        <Tabs.List px="sm">
          {steps.map((candidate, index) => (
            <Tabs.Tab
              key={candidate.value}
              value={candidate.value}
              leftSection={
                done[candidate.value] ? <IconCheck size={14} aria-label="done" /> : <Text size="xs">{index + 1}</Text>
              }
            >
              {candidate.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <RequestBar
        form={form}
        setField={setField}
        argNames={argNames}
        testValues={testValues}
        error={errors.url}
        readOnly={readOnly}
        onUseExample={applyUrlExample}
        onSend={send}
        sending={sending}
        scenarios={probeScenarios(form, testValues)}
        onTry={tryScenario}
      />

      <Box className={classes.panes}>
        <Box className={classes.left}>
          <Panel ws={ws} />
          {error && <ErrorMessage message={error} />}
          {errorList(errors).map((found, index) => (
            <Text key={index} size="xs" c="red">
              {(steps.find((candidate) => candidate.value === found.step) || steps[0]).label}: {found.message}
            </Text>
          ))}
        </Box>
        <Box className={classes.right}>
          <ResponsePane
            result={result}
            sending={sending}
            error={sendError}
            form={form}
            setField={setField}
            readOnly={readOnly}
            slots={slots}
          />
        </Box>
      </Box>

      {dialog === 'live' && (
        <SetLiveDialog
          instance={isTool ? { tool: { name } } : { contracts: [form.capability] }}
          run={setLive}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'disable' && (
        <TransitionConfirm
          confirm={{ ...confirmFor('disabled', current), action: disable }}
          onClose={() => setDialog(null)}
          onDone={() => setDialog(null)}
        />
      )}
    </Card>
  );
};

export default ConnectorWorkspace;
